import { Component, Input } from '@angular/core';
import { Asset, AssetsListService, AssetsRankDialogComponent, AssetsSearchDialogComponent, MontageBuilderService, Offer, OfferDetailService, OfferDomain, OfferProductService, OfferService, Product, SettingsService, ViewMode } from '../..';
import { MatDialog } from '@angular/material/dialog';
import { AuthorizeControlService } from 'pr1-ui-components';
import { PageEvent } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { combineLatest, distinctUntilChanged, filter, map, Subscription, take, tap } from 'rxjs';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, PreviewMode, UtilityService } from 'src/app/@shared';
import { Sort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { ActivatedRoute, Router } from '@angular/router';
import { ContextTypes } from '../../models/context-types.enum';

const DEFAULT_SEARCH_PLACEHOLDER = 'Search Assets';

@Component({
  selector: 'app-assets-list',
  templateUrl: './assets-list.component.html',
  styleUrls: ['./assets-list.component.scss']
})
export class AssetsListComponent<T extends Offer,
  TOfferDomain extends OfferDomain<Offer>> {
  @Input() context = ''; //Products, Events, Offers, Promos, OfferGroups, Brands, Versions, Coupons
  @Input() contextId = '';
  PreviewMode = PreviewMode;
  ViewMode = ViewMode;
  defaultViewMode = ViewMode.Card;
  searchPlaceholder: string = DEFAULT_SEARCH_PLACEHOLDER;
  searchQuery: string = '';

  showFirstLastButtons = true;
  pageEvent: PageEvent = new PageEvent;
  pageSizeOptions = [5, 10, 20, 50, 100, 500];


  allIdsByContext: string[] = [];
  currentUrl: string = '';
  selection = new SelectionModel<Asset>(true, [], true, (a1, a2) => a1.Detail.Id === a2.Detail.Id);
  displayedColumns = ['select', 'Image', 'AssetName', 'Montage', 'FileType', 'Group', 'DateAdded', 'DateCreated', 'Rank', 'Actions'];
  flexMediaWatcher!: Subscription;
  assets: Asset[] = [];
  viewModel$ = combineLatest([
    this.assetsListService.assets$,
    this.assetsListService.isLoading$,
    this.assetsListService.totalRecords$,
    this.assetsListService.page$,
    this.montageBuilderService.montageBuilderCheckConfigure$
  ]).pipe(
    map(([assets, isLoading, totalRecords, page, montageBuilderCheckConfigureStatus]) => {
      this.selection.clear();
      if (assets.length == 0 && page.pageIndex > 0) {
        page.previousPageIndex = 0
        page.pageIndex = 0
        this.assetsListService.page(page);
        this.assetsListService.reload();
      };
      this.assets = assets;
      const preSelectedAssets = assets.filter(asset => asset.IsFeaturedAsset);
      this.montageBuilderService.preSelectedAssetsBehaviourSubject.next(preSelectedAssets);
      return { assets, isLoading, totalRecords, page, montageBuilderCheckConfigureStatus }
    }),
  );
  selectedAssets: Asset[] = [];
  showMontage: boolean = false;
  private selectionChangeSub?: Subscription;
  constructor(
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private mediaObserver: MediaObserver,
    private authorizationService: AuthorizeControlService,
    public utilityService: UtilityService,
    private assetsListService: AssetsListService,
    private router: Router,
    private route: ActivatedRoute,
    private montageBuilderService: MontageBuilderService,
    public offerService: OfferService<T, TOfferDomain>,
    private offerProductService: OfferProductService<Product>,
    private settingsService: SettingsService,
    private offerDetailService: OfferDetailService
  ) { }
  ngOnInit() {
    this.assetsListService.context = this.context;
    this.assetsListService.contextId = this.contextId;
    this.getAllIdsByContext();
    this.detectViewportSizeChange();
    this.getOfferDetails();
    this.currentUrl = this.context;
    this.getIntegrations();
    this.offerDetailService.reload();

    // Set selection from selectedOfferAssetsBehaviorSubject if any assets exist
    const selectedAssets = this.montageBuilderService.selectedOfferAssetsBehaviorSubject.value;
    if (selectedAssets && selectedAssets.length > 0) {
      this.selection.select(...selectedAssets);
    }

    // Subscribe to selection changes and update selectedOfferAssetsBehaviorSubject
    this.selection.changed.subscribe(() => {
      this.montageBuilderService.selectedOfferAssetsBehaviorSubject.next(this.selection.selected);
    });
  }

  onFeaturedChange(checked: boolean, asset: Asset) {
    const previousValue = asset.IsFeaturedAsset;
    asset.IsFeaturedAsset = checked;
    const contextStr = this.context === 'Offers' ? 'OFFER' : 'OFFERGROUP';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Featured',
        message: `Are you sure you want to make this asset ${checked ? 'featured' : 'unfeatured'}?`,
      },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.assetsListService.setIsFeaturedByContext(contextStr, this.contextId, [asset.DomainId], checked).subscribe({
          next: () => {
            this.matSnackBar.open(`${asset.Detail.AssetName} featured status updated`, 'OK', DEFAULT_SNACKBAR_CONFIG);
          },
          error: (error) => {
            asset.IsFeaturedAsset = previousValue;
            console.error('Error updating asset featured status', error);
          }
        });
      } else {
        asset.IsFeaturedAsset = previousValue;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.selectionChangeSub) {
      this.selectionChangeSub.unsubscribe();
    }
    if (!this.contextId) {
      this.assetsListService.resetServiceState();
    }
     if (this.context === 'Products') {
      this.selection.clear();
    }
  }

  getAllIdsByContext() {
    if (this.context && this.contextId) {
      this.assetsListService.getAllIdsByContext().subscribe((ids: string[]) => {
        this.allIdsByContext = ids;
      });
    }
  }

  getIntegrations() {
    this.settingsService.integrations$.pipe(
      filter((res: any) => Array.isArray(res)), // wait until it's a valid array
      take(1)
    ).subscribe((res: any) => {
      if (res) {
        const integ = res.find((i: any) => i.ClientKey === 'montagebuilder');
        if (integ) {
          this.showMontage = integ.Status;
        }
      }
    });
  }

  canEdit(): boolean {
    return this.authorizationService.checkAccess('edit||offermang');
  }

  detectViewportSizeChange() {
    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'Image', 'AssetName', 'Actions'];
        } else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'Image', 'Montage', 'AssetName', 'FileType', 'Group', 'DateAdded', 'DateCreated', 'Rank', 'Actions'];
        } else {
          this.displayedColumns = ['select', 'Image', 'Montage', 'AssetName', 'FileType', 'Group', 'DateAdded', 'DateCreated', 'Rank', 'Actions'];
        }
      });
  }

  toggleView(mode: ViewMode) {
    this.defaultViewMode = mode;
  }

  openSearchDialog(): void {
    const dialogRef = this.dialog.open(AssetsSearchDialogComponent, {
      width: '100%',
      height: '100%',
      data: {
        // searchPlaceholder: '',
        // searchQuery: '',
        // pageSize: 50,
        // viewMode: "table", //Optional:"table"or "card"(default)
        assetsIds: Array.isArray(this.allIdsByContext) ? this.allIdsByContext : [],
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      // console.info('Assets Search Dialog Component closed!');
    })

    dialogRef.componentInstance.onSelection.subscribe((assetIds: string[]) => {
      const contextIds = (this.offerService.selection.selected && this.offerService.selection.selected.length > 0) ? this.offerService.selection.selected.map(x => x.Id) : [this.contextId];
      this.assetsListService.addAssociationsByContext(contextIds, assetIds).subscribe({
        next: () => {
          this.matSnackBar.open(`${assetIds.length > 1 ? 'Asset(s)' : 'Asset'} added`, 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.clearSearch();
          this.getAllIdsByContext();
          this.assetsListService.reload();
          this.offerDetailService.reload();
        },
        error: (error) => {
          console.error('Error adding asset', error);

          if (error.error.value) {
            throw new Error(error.error.value);
          } else {
            throw new Error(error.message);
          }
        }
      });
    });
  }

  openRankDialog(asset: Asset): void {
    const dialogRef = this.dialog.open(AssetsRankDialogComponent, {
      width: '350px',
      height: '100%',
      data: { asset: asset }
    });

    dialogRef.afterClosed().subscribe(() => {
      // console.info('Asset Rank Dialog Component closed!');
    })

    dialogRef.componentInstance.onSelection.subscribe((res: any) => {
      let asset: Asset = res.asset || null;
      const contextIds = (this.offerService.selection.selected && this.offerService.selection.selected.length > 0) ? this.offerService.selection.selected.map(x => x.Id) : [this.contextId];
      if (asset && res.rank) {
        this.assetsListService.updateAssociationRankByContext(contextIds, asset.Detail.Id as string, res.rank).subscribe({
          next: () => {
            this.matSnackBar.open(`${asset.Detail.AssetName} rank updated`, 'OK', DEFAULT_SNACKBAR_CONFIG);
            this.clearSearch();
            this.getAllIdsByContext();
            this.assetsListService.reload();
          },
          error: (error) => {
            console.error('Error updating asset rank', error);

            if (error.error.value) {
              throw new Error(error.error.value);
            } else {
              throw new Error(error.message);
            }
          }
        });
      } else {
        console.log('Asset Rank update details', res);
      }
    });
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.onSearch();
  }

  onSearch() {
    this.selection.clear();
    this.assetsListService.search(this.searchQuery);
  }

  removeAssociation(contextIds: any, assetIds: any) {
    this.assetsListService.removeAssociationsByContext(contextIds, assetIds).subscribe({
      next: async () => {
        for (var i = 0; i < assetIds.length; i++) {
          for (let index = 0; index < this.selectedAssets.length; index++) {
            if (assetIds[i] == this.selectedAssets[index].Detail.Id) {
              this.selectedAssets.splice(index, 1);
            }
          }
        }
        this.montageBuilderService.selectedOfferAssetsBehaviorSubject.next(this.selectedAssets);
        // After deleting checking with image config is there or not
        this.matSnackBar.open(`${this.selection.selected.length > 1 ? 'Asset(s)' : 'Asset'} deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
        this.clearSearch();
        this.getAllIdsByContext();
        this.assetsListService.reload();
        this.getOfferDetails();
        this.offerDetailService.reload();
      },
      error: (error) => {
        console.error('Error deleting asset', error);

        if (error.error.value) {
          throw new Error(error.error.value);
        } else {
          throw new Error(error.message);
        }
      }
    });
  }

  onDelete() {
    if (this.selection.selected.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove ${this.selection.selected.length > 1 ? 'Asset(s)' : 'Asset'}?`,
        },
        disableClose: true,
      });

      confirmDialog.afterClosed().subscribe(
        confirmResult => {
          if (confirmResult) {
            const contextIds = (this.offerService.selection.selected && this.offerService.selection.selected.length > 0) ? this.offerService.selection.selected.map(x => x.Id) : [this.contextId];
            const assetIds = this.selection.selected.map((asset: any) => asset.Detail.Id);
            const montageAssetSelected = this.selection.selected.find(asset => asset.Detail.AssetName.startsWith('M_'));
            if(montageAssetSelected) {
              const request = {
                Detail: {
                  Id: this.montageBuilderService.montageBuilderResponseBehaviorSubject.value?.ImageEditorConfigId || montageAssetSelected.Detail.Id
                },
                [this.context === "Offers" ? 'OfferId' : 'OfferGroupId']:this.contextId
              }
              this.montageBuilderService.deleteMontageImageConfig(request).subscribe({
                next: (res: any) => {
                  this.matSnackBar.open(`Montage deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
                  this.removeAssociation(contextIds, assetIds);
                },
                error: (error) => {
                  console.error('Error deleting montage', error);

                  if (error.error.value) {
                    throw new Error(error.error.value);
                  } else {
                    throw new Error(error.message);
                  }
                }
              })
            } else {
              this.removeAssociation(contextIds, assetIds);
            }
          }
        });
    }
  }

  getOfferDetails() {
    if (!this.contextId) return;

    if (this.context === 'Offers') {
      this.offerService.getOffer(this.contextId).pipe(take(1))
        .subscribe(offer => this.processOfferResponse(offer));
    }

    if (this.context === 'OfferGroups') {
      this.offerService.getOfferGroup(this.contextId).pipe(take(1))
        .subscribe(offerGroup => this.processOfferResponse(offerGroup));
    }
  }

  private processOfferResponse(response: any): void {
    if (!response) return;
    this.montageBuilderService.selectedOfferDetailsBehaviorSubject.next(response);
    const hasImageConfig = !!response.ImageEditorConfigId;
    this.montageBuilderService.montageBuilderCheckConfigureBehaviorSubject.next(hasImageConfig);
    this.montageBuilderService.montageBuilderResponseBehaviorSubject.next(response);
  }

  onSort(sortState: Sort): void {
    this.assetsListService.sort(sortState);
    this.selection.clear();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.selection.clear();
    this.assetsListService.page(pageEvent);
  }

  toggleSelect(checked: boolean, asset: any) {
    if (checked) {
      if (!this.selection.isSelected(asset)) {
        this.selection.select(asset);
      }
    } else {
      this.selection.deselect(asset);
    }
  }

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.assets.forEach(item => {
        if (!this.selection.isSelected(item)) {
          this.selection.select(item);
        }
      });
    } else {
      this.assets.forEach(item => {
        this.selection.deselect(item);
      });
    }
  }

  isAllSelectedOnPage(): boolean {
    return this.assets.every(item => this.selection.isSelected(item));
  }

  isSomeSelectedOnPage(): boolean {
    return this.assets.some(item => this.selection.isSelected(item));
  }

}
